import { differenceInCalendarYears, parseISO } from 'date-fns'
import { format } from 'date-fns-tz'
import { companyMessage } from '../assets/i18n/i18n'
import { CodeableConcept } from '../types/FHIRTypes/CodeableConcept'
import { HumanName } from '../types/FHIRTypes/HumanName'
import { Extension } from '../types/FHIRTypes/Extension'
import { Identifier } from '../types/FHIRTypes/Identifier'
import { Reference } from '../types/FHIRTypes/Reference'
import { ExtensionValueKey } from '../types/FHIRTypes/ExtensionsValueKeys'
import { ExtensionSliceMap } from '../types/ExtensionSliceMap'
import { FormikErrors } from 'formik'

export const ifExists = (data: any, key: string): string => {
  if (data) {
    return data[0][key]
  }
  return ''
}

export const numberWithCommas = (x: number): string => x !== undefined ? x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''

export const calculateAge = (dob: Date): number => {
  const today = new Date()
  const birthDate = new Date(dob)
  let age = differenceInCalendarYears(today, birthDate)
  const monthDifference = today.getMonth() - birthDate.getMonth()
  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1
  }
  return age
}

export const prepareValueForDb = (string: string): string | null => string || null

export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url)
  } catch (error) {
    return false
  }

  return true
}

export const capitalizeFirstLetter = (string: string | undefined): string => {
  let newString = ''
  if (string) {
    newString = string.charAt(0).toUpperCase() + string.slice(1).toLowerCase()
  }
  return newString
}

export const formatIdentifier = (identifier: any): string => {
  if (!identifier?.value) {
    return ''
  }
  const prefix = identifier.display || identifier.type?.text || ''
  return prefix ? `${prefix}: ${identifier.value}` : identifier.value
}


export const formatDate = (date: string): string => date ? format(new Date(date), 'MM/dd/yyyy') : ''

export const formatDateWithFormat = (date: string, fmt: string): string => date ? format(new Date(date), fmt) : ''

export const formatDateInTimeZone = (date: string, fmt: string, tz: string): string => format(parseISO(date), fmt, { timeZone: tz })

export const formatDateTime = (date: string): string => date ? format(new Date(date), 'MM/dd/yyyy h:mm a') : ''

export const sum = (a: number, b: number): number => a + b

export const getUrlParameter = (name: string, url: string): string => {
  name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]')
  const regex = new RegExp('[\\?&]' + name + '=([^&#]*)')
  const results = regex.exec(url)
  return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '))
}

export const formatDollar = (value: number): string => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  })

  return formatter.format(value)
}

export function buildPhone(providerPhone: any): Array<string> {
  const phone = providerPhone
  if (phone?.length === 10) {
    return phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
  }
  return phone
}

export const getToken = (): string => {
  let token = ''
  const usertoken = sessionStorage.getItem('auth-token')
  if (usertoken) {
    token = JSON.parse(usertoken)
  }
  return token
}

export const testStringWithRegex = (str: string | undefined, regex: RegExp): boolean => str ? regex.test(str) : false

export const addCompanyPhoneMessage = (str: string): string => str ? str.replace('<phone>', companyMessage.ContactPhone) : ''

export const handleCodeableConcept = (codeableConcept: CodeableConcept | CodeableConcept[] | undefined): string => {
  if (!codeableConcept) return ''

  const displayValue = (codeableConcept: CodeableConcept): string => {
    const codingDisplays = codeableConcept.coding?.map(coding => coding.display || coding.code).filter(Boolean) || []
    return codingDisplays.length > 0 ? codingDisplays.join(', ') : codeableConcept.text || ''
  }

  return Array.isArray(codeableConcept) ? codeableConcept.map(displayValue).join(', ') : displayValue(codeableConcept)
}

export const handleReference = (reference: Reference | Reference[] | undefined): string => {
  if (!reference) return ''

  const displayValue = (reference: Reference): string =>
    reference.display || reference.reference || reference.identifier?.value || ''

  return Array.isArray(reference) ? reference.map(displayValue).join(', ') : displayValue(reference)
}

export const handleExtension = (extension: Extension | Extension[] | undefined): string => {
  // we'll eventually need more delicate handling of different types of extensions but this can be a start so that we have something to display
  if (!extension) return ''

  const extractDisplay = (value: any, label = ''): string => {
    if (Array.isArray(value)) {
      return value.map(item => extractDisplay(item, label)).join(', ')
    } else if (typeof value === 'object' && value !== null) {
      return Object.entries(value)
        .map(([key, val]) => `${capitalizeFirstLetter(key)}: ${extractDisplay(val, key)}`)
        .join(', ')
    } else {
      return capitalizeFirstLetter(value.toString())
    }
  }

  const displayValue = (extension: Extension): string => {
    for (const key in extension) {
      if (extension.hasOwnProperty(key) && key.startsWith('value') && (extension as any)[key] !== undefined) {
        const value = (extension as any)[key]
        return typeof value === 'object' || Array.isArray(value)
          ? extractDisplay(value, key)
          : capitalizeFirstLetter(value.toString())
      }
    }
    return ''
  }

  return Array.isArray(extension) ? extension.map(displayValue).join(', ') : displayValue(extension)
}

export const handleIdentifier = (identifiers: Identifier[] | undefined): string => {
  if (!identifiers) return ''

  return identifiers
    .map((identifier) => {
      const system = identifier.system ? identifier.system : ''
      const type = identifier.type ? handleCodeableConcept(identifier.type) : ''
      const value = identifier.value ? identifier.value : ''

      return type ? `${type}: ${value}` : system ? `${system}: ${value}` : value
    })
    .join(', ')
}

export const buildPeriod = (period: { start?: string; end?: string } | undefined): string => {
  if (!period) return ''

  const start = period?.start ? formatDateInTimeZone(period?.start, 'MM/dd/yyyy', 'UTC') : null
  const end = period?.end ? formatDateInTimeZone(period?.end, 'MM/dd/yyyy', 'UTC') : null

  if (start && end) {
    return `${start} - ${end}`
  } else if (start) {
    return `${start} - Ongoing`
  } else if (end) {
    return `Unknown - ${end}`
  } else {
    return ''
  }
}

export const scrollToTop = (): void => window.scrollTo({ top: 0, behavior: 'smooth' })

export const buildHumanName = (names: HumanName[] | undefined): string => {
  if (!names) return ''

  const name = names.find((name) => name.use === 'official') || names[0]
  const prefix = name.prefix ? name.prefix.join(' ') + ' ' : ''
  const given = name.given ? name.given.join(' ') : ''
  const family = name.family || ''
  const suffix = name.suffix ? name.suffix.join(' ') : ''

  return `${prefix}${given} ${family} ${suffix}`.trim()
}

export const findExtensionByURI = (extensions: Extension[] | undefined, uri: string): Extension => {
  if (!extensions) return {} as Extension

  return extensions.find((extension) => extension.url === uri) || {} as Extension
}

export const filterExtensionsByUrl = (extensions: Extension[] | undefined, url: string): Extension[] => {
  return (extensions || []).filter((extension) => extension.url === url)
}


export function renderFormikError(
  error: string | string[] | FormikErrors<any> | FormikErrors<any>[] | undefined
): string | null {
  if (!error) return null

  if (typeof error === 'string') {
    return error
  }

  if (Array.isArray(error)) {
    return (error).join(', ')
  }

  return null
}

export const buildExtSlices = (
  extensions: Extension[] | undefined,
  resourceType: keyof typeof ExtensionSliceMap
): Record<string, any[]> => {
  const resourceSpecificMapping = ExtensionSliceMap[resourceType] || {}

  const extractValues = (
    extensions: Extension[] | undefined,
    url: string,
    valueKey: ExtensionValueKey
  ): any[] => {
    if (!extensions) return []

    return extensions.flatMap((extension) => {
      const values: any[] = []

      // Check if the current extension matches the URL and extract the value
      if (extension.url === url && valueKey in extension) {
        values.push(extension[valueKey])
      }

      // Recursively process nested extensions, if any
      if (extension.extension) {
        values.push(...extractValues(extension.extension, url, valueKey))
      }

      return values
    })
  }

  return Object.fromEntries(
    Object.entries(resourceSpecificMapping).map(([key, { url, valueKey }]) => [
      key,
      extractValues(extensions, url, valueKey as ExtensionValueKey),
    ])
  )
}

export const isDemoTenant = (tenant: string): boolean => tenant.toLowerCase() === 'demo'